import { BUSINESS_LOCALITY, TRUST_FACTS } from '../constants/site';
import { LEARN_ARTICLE_CONTENT } from './learnArticles';

const sharedHowItWorks = [
  'Apply online in under 60 seconds with core business and asset details.',
  'We match your file to lenders aligned to your asset, profile, and timing.',
  'Receive conditional approval and structure options from aligned lenders.',
  'Settle and fund fast so your business can put the asset to work.',
];

const sharedEligibility = [
  'Australian ABN or ACN holders (sole traders, companies, trusts).',
  'Asset used primarily for business purposes.',
  'Demonstrated serviceability from business cashflow or supporting income.',
  'Identification and basic business documentation available.',
  'Credit profile assessed case-by-case, including low-doc pathways.',
];

const sharedDifferentiators = [
  'Fast lender matching across specialist and major lenders.',
  'No upfront broker fee for standard commercial applications where applicable under your engagement terms.',
  'Scenario-led structuring for term, balloon, and repayment cadence.',
  'Dedicated broker support from enquiry through settlement.',
  'Australian market expertise across transport, trades, and operating businesses.',
];

const defaultRatesRows = [
  { label: 'Indicative rate range', value: 'Varies by lender, profile, and asset.' },
  { label: 'Typical term', value: 'Often between 24 and 84 months, depending on scenario.' },
  { label: 'Typical balloon', value: 'Balloon options may be available subject to lender policy.' },
  { label: 'Establishment fee', value: 'May apply depending on lender and product.' },
  { label: 'Ongoing fees', value: 'May apply depending on lender and structure.' },
];

const cityMeta = {
  sydney: {
    city: 'Sydney',
    state: 'NSW',
    stateName: 'New South Wales',
    industries: ['logistics', 'construction', 'civil works', 'wholesale trade', 'professional services'],
    suburbs: ['Parramatta', 'Penrith', 'Liverpool', 'Alexandria', 'Blacktown'],
    context: 'Sydney operators often manage higher site access costs, fast project turnarounds, and mixed metro-regional deployment.',
  },
  melbourne: {
    city: 'Melbourne',
    state: 'VIC',
    stateName: 'Victoria',
    industries: ['logistics', 'hospitality', 'construction', 'manufacturing', 'trade services'],
    suburbs: ['Dandenong', 'Sunshine', 'Craigieburn', 'Geelong', 'Truganina'],
    context: 'Melbourne SMEs commonly require staged equipment rollouts and finance structures aligned with seasonal or contract-based demand.',
  },
  brisbane: {
    city: 'Brisbane',
    state: 'QLD',
    stateName: 'Queensland',
    industries: ['transport', 'construction', 'trade services', 'hospitality', 'health services'],
    suburbs: ['Acacia Ridge', 'North Lakes', 'Ipswich', 'Logan', 'Cleveland'],
    context: 'Brisbane businesses frequently balance metro growth opportunities with regional work corridors across south-east Queensland.',
  },
  perth: {
    city: 'Perth',
    state: 'WA',
    stateName: 'Western Australia',
    industries: ['mining support', 'construction', 'transport', 'engineering', 'field services'],
    suburbs: ['Welshpool', 'Osborne Park', 'Joondalup', 'Kwinana', 'Midland'],
    context: 'Perth operators often need robust funding timelines to support remote deployment, mining-adjacent contracts, and long asset duty cycles.',
  },
  adelaide: {
    city: 'Adelaide',
    state: 'SA',
    stateName: 'South Australia',
    industries: ['manufacturing', 'transport', 'food production', 'construction', 'trade services'],
    suburbs: ['Mawson Lakes', 'Port Adelaide', 'Salisbury', 'Elizabeth', 'Mile End'],
    context: 'Adelaide businesses often prioritise predictable repayments and low-friction approvals to manage lean operating teams.',
  },
  canberra: {
    city: 'Canberra',
    state: 'ACT',
    stateName: 'the ACT',
    industries: ['government contractors', 'professional services', 'construction', 'logistics', 'IT services'],
    suburbs: ['Fyshwick', 'Belconnen', 'Tuggeranong', 'Gungahlin', 'Mitchell'],
    context: 'Canberra firms commonly seek finance solutions aligned to tender cycles and staged procurement across multi-quarter projects.',
  },
  hobart: {
    city: 'Hobart',
    state: 'TAS',
    stateName: 'Tasmania',
    industries: ['transport', 'marine services', 'construction', 'tourism operations', 'trade services'],
    suburbs: ['Glenorchy', 'Kingston', 'Moonah', 'Rosny', 'Sorell'],
    context: 'Hobart operators often require practical repayment structures that account for seasonal revenue swings and mixed regional workloads.',
  },
  darwin: {
    city: 'Darwin',
    state: 'NT',
    stateName: 'the Northern Territory',
    industries: ['civil works', 'transport', 'mining support', 'defence services', 'trade services'],
    suburbs: ['Berrimah', 'Winnellie', 'Palmerston', 'Humpty Doo', 'Howard Springs'],
    context: 'Darwin businesses regularly need durable financing for high-utilisation assets servicing regional and remote projects.',
  },
};

const makeFaqs = (topic) => [
  { q: `How quickly can ${topic} be approved?`, a: 'It depends on the lender, the size and complexity of the deal, and how complete your documents are. Straightforward applications with everything supplied up front are usually the quickest to reach a decision.' },
  { q: `Are low-doc options available for ${topic}?`, a: 'Some lenders offer low-doc pathways for established ABN holders who can’t provide full financials. Eligibility depends on your ABN history, credit profile, the asset and the amount, and differs between lenders.' },
  { q: `Do I need a deposit for ${topic}?`, a: 'Not always. Deposit requirements vary by lender, asset age and your business profile. Stronger applications may qualify with no deposit, while newer businesses or older assets often need a contribution.' },
  { q: `What loan terms are common for ${topic}?`, a: 'Terms are usually matched to the working life of the asset and your cashflow, and are confirmed with the lender when the deal is structured.' },
  { q: 'Can I include a balloon payment?', a: 'Often, yes. A balloon (residual) lowers your regular repayments but leaves a lump sum to pay at the end of the term, so it needs to suit your cashflow and plans for the asset.' },
  { q: 'Will enquiring affect my credit file?', a: 'Talking to us about your scenario does not create a credit enquiry. A credit enquiry is only made when you agree to proceed with a formal application to a lender.' },
  { q: 'Which documents are usually required?', a: 'Common items include identification, ABN or ACN details, bank statements and the asset invoice or quote. Some lenders ask for financial statements or tax returns, depending on the deal.' },
  { q: 'How should I compare lender options?', a: 'Compare the total cost including fees, the repayment structure, any balloon, early payout terms and settlement timing, not just the headline rate.' },
];
const withArticle = (topic) => {
  if (!/(loan|mortgage)$/.test(topic)) return topic;
  return `${/^[aeiou]/.test(topic) ? 'an' : 'a'} ${topic}`;
};

const makeCommercialPage = ({
  path,
  h1,
  title,
  description,
  valueProp,
  primaryKeyword,
  type,
  parentPath = null,
  whatWeFinance,
  relatedCities,
  relatedLearnSlugs,
  faqTopic,
}) => ({
  path,
  h1,
  title,
  description,
  valueProp,
  primaryKeyword,
  type,
  parentPath,
  howItWorks: sharedHowItWorks,
  whatWeFinance,
  ratesRows: defaultRatesRows,
  eligibility: sharedEligibility,
  differentiators: sharedDifferentiators,
  faqs: makeFaqs(faqTopic || withArticle(primaryKeyword.replace(/ Australia$/, '').toLowerCase())),
  relatedCities,
  relatedLearnSlugs,
  trust: TRUST_FACTS,
});

export const PILLAR_PAGES = [
  makeCommercialPage({
    path: '/truck-and-trailer-finance',
    h1: 'Compare Truck and Trailer Finance Australia | Fast Asset Matching | Flash Funding',
    title: 'Truck and Trailer Finance | Australia | Flash Funding',
    description: 'Compare truck and trailer finance options in Australia. Get a quote fast and match with lenders aligned to transport operators.',
    valueProp: 'Fast lender matching for owner-operators, fleets, and transport SMEs nationwide.',
    primaryKeyword: 'Truck and trailer finance Australia',
    type: 'pillar',
    whatWeFinance: [
      { label: 'Prime Mover Finance', path: '/truck-finance/prime-mover-finance' },
      { label: 'Rigid Truck Finance', path: '/truck-finance/rigid-truck-finance' },
      { label: 'Trailer Finance', path: '/truck-finance/trailer-finance' },
      { label: 'Used Truck Finance', path: '/truck-finance/used-truck-finance' },
    ],
    relatedCities: ['/locations/sydney', '/locations/melbourne', '/locations/brisbane', '/locations/perth'],
    relatedLearnSlugs: ['how-to-get-truck-finance-owner-operator-australia', 'used-truck-finance-what-lenders-look-for', 'abn-truck-finance-under-2-years-guide'],
  }),
  makeCommercialPage({
    path: '/equipment-finance',
    h1: 'Compare Equipment Finance Australia | Fast Decisions | Flash Funding',
    title: 'Equipment Finance Australia | Fast Decisions | Flash Funding',
    description: 'Compare equipment finance in Australia for construction, yellow goods, and business assets. Apply now and get a fast quote.',
    valueProp: 'Structured equipment funding for operators who need approvals without process drag.',
    primaryKeyword: 'Equipment finance Australia',
    type: 'pillar',
    whatWeFinance: [
      { label: 'Excavator Finance', path: '/equipment-finance/excavator-finance' },
      { label: 'Bobcat Finance', path: '/equipment-finance/bobcat-finance' },
      { label: 'Earthmoving Equipment Finance', path: '/equipment-finance/earthmoving-equipment-finance' },
      { label: 'Yellow Goods Finance', path: '/equipment-finance/yellow-goods-finance' },
    ],
    relatedCities: ['/locations/brisbane', '/locations/perth', '/locations/adelaide', '/locations/darwin'],
    relatedLearnSlugs: ['equipment-finance-small-business-2026-guide', 'excavator-finance-new-vs-used-guide', 'balloon-payments-equipment-finance-guide'],
  }),
  makeCommercialPage({
    path: '/vehicle-finance',
    h1: 'Compare Vehicle Finance Australia | Business Vehicle Specialists | Flash Funding',
    title: 'Vehicle Finance Australia | Business Vehicle Specialists | Flash Funding',
    description: 'Apply for vehicle finance in Australia for cars, utes, vans, and light commercial vehicles. Compare lender options in minutes.',
    valueProp: 'Vehicle funding for operators, tradies, and fleet buyers who need speed and clarity.',
    primaryKeyword: 'Vehicle finance Australia',
    type: 'pillar',
    whatWeFinance: [
      { label: 'Business Car Loan', path: '/vehicle-finance/business-car-loan' },
      { label: 'Ute Finance', path: '/vehicle-finance/ute-finance' },
      { label: 'Van Finance', path: '/vehicle-finance/van-finance' },
      { label: 'Light Commercial Vehicle Finance', path: '/vehicle-finance/light-commercial-vehicle-finance' },
    ],
    relatedCities: ['/locations/sydney', '/locations/melbourne', '/locations/canberra', '/locations/hobart'],
    relatedLearnSlugs: ['business-car-loan-vs-novated-lease-sole-traders', 'how-long-asset-finance-approval-takes-australia', 'low-doc-asset-finance-who-qualifies'],
  }),
  makeCommercialPage({
    path: '/business-loans',
    h1: 'Compare Business Loans Australia | Working Capital Solutions | Flash Funding',
    title: 'Business Loans Australia | Working Capital Solutions | Flash Funding',
    description: 'Compare business loans and working capital options in Australia. Apply online and get matched to the right lender quickly.',
    valueProp: 'Working capital and growth lending for Australian SMEs and trade-led businesses.',
    primaryKeyword: 'Business loans Australia',
    type: 'pillar',
    whatWeFinance: [
      { label: 'Unsecured Business Loan', path: '/business-loans/unsecured-business-loan' },
      { label: 'Working Capital Loan', path: '/business-loans/working-capital-loan' },
      { label: 'Equipment Loan', path: '/business-loans/equipment-loan' },
      { label: 'Chattel Mortgage', path: '/business-loans/chattel-mortgage' },
    ],
    relatedCities: ['/locations/sydney', '/locations/brisbane', '/locations/perth', '/locations/melbourne'],
    relatedLearnSlugs: ['working-capital-loans-trades-construction', 'what-is-asset-finance-australia-guide', 'chattel-mortgage-vs-novated-vs-hire-purchase'],
  }),
];

export const SUB_PRODUCT_PAGES = [
  { path: '/truck-finance/prime-mover-finance', parentPath: '/truck-and-trailer-finance', keyword: 'Prime mover finance Australia' },
  { path: '/truck-finance/rigid-truck-finance', parentPath: '/truck-and-trailer-finance', keyword: 'Rigid truck finance Australia' },
  { path: '/truck-finance/trailer-finance', parentPath: '/truck-and-trailer-finance', keyword: 'Trailer finance Australia' },
  { path: '/truck-finance/used-truck-finance', parentPath: '/truck-and-trailer-finance', keyword: 'Used truck finance Australia' },
  { path: '/equipment-finance/excavator-finance', parentPath: '/equipment-finance', keyword: 'Excavator finance Australia' },
  { path: '/equipment-finance/bobcat-finance', parentPath: '/equipment-finance', keyword: 'Bobcat finance Australia' },
  { path: '/equipment-finance/earthmoving-equipment-finance', parentPath: '/equipment-finance', keyword: 'Earthmoving equipment finance Australia' },
  { path: '/equipment-finance/yellow-goods-finance', parentPath: '/equipment-finance', keyword: 'Yellow goods finance Australia' },
  { path: '/vehicle-finance/business-car-loan', parentPath: '/vehicle-finance', keyword: 'Business car loan Australia' },
  { path: '/vehicle-finance/ute-finance', parentPath: '/vehicle-finance', keyword: 'Ute finance Australia' },
  { path: '/vehicle-finance/van-finance', parentPath: '/vehicle-finance', keyword: 'Van finance Australia' },
  { path: '/vehicle-finance/light-commercial-vehicle-finance', parentPath: '/vehicle-finance', keyword: 'Light commercial vehicle finance Australia' },
  { path: '/business-loans/unsecured-business-loan', parentPath: '/business-loans', keyword: 'Unsecured business loan Australia' },
  { path: '/business-loans/working-capital-loan', parentPath: '/business-loans', keyword: 'Working capital loan Australia' },
  { path: '/business-loans/equipment-loan', parentPath: '/business-loans', keyword: 'Equipment loan Australia' },
  { path: '/business-loans/chattel-mortgage', parentPath: '/business-loans', keyword: 'Chattel mortgage Australia' },
].map((item) => {
  const label = item.path.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  const parent = PILLAR_PAGES.find((p) => p.path === item.parentPath);
  const siblings = PILLAR_PAGES.find((p) => p.path === item.parentPath)?.whatWeFinance || [];

  return makeCommercialPage({
    path: item.path,
    h1: `Compare ${label} Australia | Specialist Broker Support | Flash Funding`,
    title: `${label} Australia | Flash Funding`,
    description: `Compare ${item.keyword.replace(/ Australia$/, '').toLowerCase()} options in Australia and get a quote in 60 seconds with lender-matching support from Flash Funding.`,
    valueProp: `Commercial structuring support for ${label.toLowerCase()} deals across Australia.`,
    primaryKeyword: item.keyword,
    type: 'subproduct',
    parentPath: item.parentPath,
    whatWeFinance: siblings,
    relatedCities: ['/locations/sydney', '/locations/melbourne', '/locations/brisbane'],
    relatedLearnSlugs: parent?.relatedLearnSlugs || [],
  });
});

const buildCityContent = (cityConfig) => [
  `Flash Funding arranges asset finance and business lending for businesses in ${cityConfig.city} and across ${cityConfig.stateName}. We're based in ${BUSINESS_LOCALITY}.`,
  `We can help ${cityConfig.city} businesses finance trucks and trailers, construction and earthmoving equipment, business vehicles and working capital. Lenders assess your business, the asset and your ability to repay rather than your postcode, so ${cityConfig.city} clients have access to the same lender panel as every other client.`,
  'Tell us about the asset, the amount and your timing using the quote button above, and we will come back to you with options that suit your situation.',
];

export const CITY_PAGES = Object.values(cityMeta).map((item) =>
  makeCommercialPage({
    path: `/locations/${item.city.toLowerCase()}`,
    h1: `Asset Finance ${item.city} | Trucks, Equipment & Vehicles | Flash Funding`,
    title: `Asset Finance ${item.city} | Flash Funding`,
    description: `Compare asset finance in ${item.city} for trucks, equipment, vehicles, and business growth funding. Apply now and get a quote fast.`,
    valueProp: `Broker support for ${item.city} businesses financing trucks, equipment, vehicles and working capital.`,
    primaryKeyword: `Asset finance ${item.city}`,
    faqTopic: `asset finance in ${item.city}`,
    type: 'city',
    whatWeFinance: [
      { label: 'Truck and Trailer Finance', path: '/truck-and-trailer-finance' },
      { label: 'Equipment Finance', path: '/equipment-finance' },
      { label: 'Vehicle Finance', path: '/vehicle-finance' },
      { label: 'Business Loans', path: '/business-loans' },
    ],
    relatedCities: Object.values(cityMeta).filter((c) => c.city !== item.city).slice(0, 4).map((c) => `/locations/${c.city.toLowerCase()}`),
    relatedLearnSlugs: ['what-is-asset-finance-australia-guide', 'how-long-asset-finance-approval-takes-australia', 'low-doc-asset-finance-who-qualifies'],
  })
).map((cityPage) => {
  const cityKey = cityPage.path.split('/').pop();
  const cityConfig = cityMeta[cityKey];
  return {
    ...cityPage,
    localContent: buildCityContent(cityConfig),
  };
});

export const ALL_COMMERCIAL_PAGES = [...PILLAR_PAGES, ...SUB_PRODUCT_PAGES, ...CITY_PAGES];

export const COMMERCIAL_PAGES_BY_PATH = Object.fromEntries(ALL_COMMERCIAL_PAGES.map((page) => [page.path, page]));

// Legacy templated body, shown only until each article is rewritten (those pages are noindexed).
const longSection = (topic, angle) => `${topic} in Australia is rarely a one-variable decision. Operators usually need to balance approval speed, total cost, repayment shape, and asset lifecycle risk at the same time. In practical terms, this means comparing more than headline rates and looking closely at structure, policy fit, and settlement conditions.

From a broker perspective, ${angle}. A scenario-led approach helps avoid expensive mismatches, especially where contract timelines, utilisation assumptions, and business cashflow vary through the year.

For SMBs and owner-operators, the strongest outcomes usually come from clear documentation, accurate position statements, and lender targeting based on policy appetite rather than generic applications.

Before proceeding, validate term length, balloon settings, and fee treatment against your operating model. This keeps the funding structure commercially usable after settlement, not just theoretically attractive at approval stage.`;

const legacySections = (post) => [
  { heading: 'Why this topic matters in Australian commercial lending', content: longSection(post.title, `this topic directly impacts approval quality and settlement outcomes for ${post.targetKeyword}`) },
  { heading: 'How lenders assess this scenario', content: longSection(post.title, `lenders usually review serviceability, asset quality, and business profile together rather than in isolation`) },
  { heading: 'Structuring choices that change your result', content: longSection(post.title, `structure selection can materially affect tax timing, cashflow pressure, and refinance flexibility`) },
  { heading: 'Common mistakes and how to avoid them', content: longSection(post.title, `most avoidable issues come from missing documents, unrealistic residuals, or late-stage lender switching`) },
  { heading: 'Practical checklist before you apply', content: longSection(post.title, `a practical pre-lodgement checklist can cut turnaround time and improve lender confidence`) },
  { heading: 'Next-step strategy for business operators', content: longSection(post.title, `operators who model multiple options before submission generally secure stronger long-term outcomes`) },
];

const PILLAR_LABELS = {
  '/business-loans/chattel-mortgage': 'Chattel mortgage finance',
  '/truck-and-trailer-finance': 'Truck and trailer finance',
  '/equipment-finance': 'Equipment finance',
  '/truck-finance/used-truck-finance': 'Used truck finance',
  '/equipment-finance/excavator-finance': 'Excavator finance',
  '/vehicle-finance/business-car-loan': 'Business car loans',
  '/low-doc-asset-finance': 'Low-doc asset finance',
  '/business-loans/working-capital-loan': 'Working capital loans',
};

export const LEARN_POSTS = [
  {
    slug: 'chattel-mortgage-vs-novated-vs-hire-purchase',
    title: 'Chattel Mortgage vs Novated Lease vs Hire Purchase: Which Is Right for Your Business?',
    pillarPath: '/business-loans/chattel-mortgage',
    targetKeyword: 'chattel mortgage vs novated lease vs hire purchase',
  },
  {
    slug: 'how-to-get-truck-finance-owner-operator-australia',
    title: 'How to Get Truck Finance as an Owner-Operator in Australia',
    pillarPath: '/truck-and-trailer-finance',
    targetKeyword: 'truck finance owner-operator Australia',
  },
  {
    slug: 'equipment-finance-small-business-2026-guide',
    title: 'Equipment Finance for Small Business: A 2026 Guide',
    pillarPath: '/equipment-finance',
    targetKeyword: 'equipment finance small business Australia',
  },
  {
    slug: 'what-is-asset-finance-australia-guide',
    title: 'What Is Asset Finance and How Does It Work in Australia?',
    pillarPath: '/equipment-finance',
    targetKeyword: 'what is asset finance Australia',
  },
  {
    slug: 'used-truck-finance-what-lenders-look-for',
    title: 'Used Truck Finance: What Lenders Actually Look For',
    pillarPath: '/truck-finance/used-truck-finance',
    targetKeyword: 'used truck finance lender criteria',
  },
  {
    slug: 'excavator-finance-new-vs-used-guide',
    title: 'Excavator Finance: New vs Used and How to Choose',
    pillarPath: '/equipment-finance/excavator-finance',
    targetKeyword: 'excavator finance new vs used',
  },
  {
    slug: 'business-car-loan-vs-novated-lease-sole-traders',
    title: 'Business Car Loan vs Novated Lease for Sole Traders',
    pillarPath: '/vehicle-finance/business-car-loan',
    targetKeyword: 'business car loan vs novated lease sole trader',
  },
  {
    slug: 'how-long-asset-finance-approval-takes-australia',
    title: 'How Long Does Asset Finance Approval Take in Australia?',
    pillarPath: '/equipment-finance',
    targetKeyword: 'asset finance approval time Australia',
  },
  {
    slug: 'low-doc-asset-finance-who-qualifies',
    title: 'Low-Doc Asset Finance: What It Is and Who Qualifies',
    pillarPath: '/low-doc-asset-finance',
    targetKeyword: 'low-doc asset finance Australia',
  },
  {
    slug: 'working-capital-loans-trades-construction',
    title: 'Working Capital Loans for Trades and Construction Businesses',
    pillarPath: '/business-loans/working-capital-loan',
    targetKeyword: 'working capital loans trades construction',
  },
  {
    slug: 'balloon-payments-equipment-finance-guide',
    title: 'Balloon Payments on Equipment Finance: How They Work',
    pillarPath: '/equipment-finance',
    targetKeyword: 'balloon payments equipment finance',
  },
  {
    slug: 'abn-truck-finance-under-2-years-guide',
    title: 'ABN Truck Finance: A Guide for New Businesses Under 2 Years Old',
    pillarPath: '/truck-and-trailer-finance',
    targetKeyword: 'ABN truck finance under 2 years',
  },
].map((basePost, index, all) => {
  const post = { ...basePost, ...(LEARN_ARTICLE_CONTENT[basePost.slug] || {}) };
  const siblingOne = all[(index + 1) % all.length];
  const siblingTwo = all[(index + 2) % all.length];
  const siblingThree = all[(index + 3) % all.length];
  return {
    ...post,
    metaTitle: post.title,
    metaDescription: post.description || `${/[?.!]$/.test(post.title) ? post.title : `${post.title}.`} Practical guidance for Australian businesses from Flash Funding.`,
    authorSlug: 'andrew-sunna',
    // Articles stay out of the index until each one is rewritten and has real sections.
    noindex: !post.sections,
    updated: post.updated || null,
    faqs: post.faqs || [],
    sections: post.sections || legacySections(post),
    internalLinks: [
      { label: PILLAR_LABELS[post.pillarPath] || 'Related finance options', path: post.pillarPath },
      { label: siblingOne.title, path: `/learn/${siblingOne.slug}` },
      { label: siblingTwo.title, path: `/learn/${siblingTwo.slug}` },
      { label: siblingThree.title, path: `/learn/${siblingThree.slug}` },
      { label: 'Asset Finance Calculator', path: '/calculators/asset-finance-calculator' },
    ],
  };
});

export const LEARN_POSTS_BY_SLUG = Object.fromEntries(LEARN_POSTS.map((post) => [post.slug, post]));

export const AUTHORS = [
  {
    slug: 'andrew-sunna',
    name: 'Andrew Sunna',
    credentials: 'Founder and Managing Director, Flash Funding',
    photo: '/team/andrew-sunna.jpg',
    bio: `Andrew Sunna is the Founder and Managing Director of Flash Funding, based in ${BUSINESS_LOCALITY}, with 19 years of experience in the finance industry. He arranges asset, equipment and vehicle finance and business loans for Australian businesses, working with a panel of bank and non-bank lenders. Flash Funding Group Pty Ltd is an Authorised Credit Representative (ACR 578970) of Australian Credit Licence 414426 held by AFAS Group Pty Ltd.`,
  },
];

// Old placeholder author URLs now point to the real author.
export const LEGACY_AUTHOR_SLUGS = ['alex-hart', 'jamie-cole'];

export const AUTHORS_BY_SLUG = Object.fromEntries(AUTHORS.map((author) => [author.slug, author]));

export const ALL_RETROFIT_PATHS = [
  ...ALL_COMMERCIAL_PAGES.map((p) => p.path),
  '/locations',
  '/learn',
  ...LEARN_POSTS.map((p) => `/learn/${p.slug}`),
  '/reviews',
  '/how-it-works',
  '/disclosure',
  ...AUTHORS.map((a) => `/authors/${a.slug}`),
  '/calculators/asset-finance-calculator',
  '/calculators/chattel-mortgage-calculator',
  '/calculators/business-loan-repayment-calculator',
];
